import { useWindowOpen } from '@repo/platform';
import { useRouter } from 'next/router';
import { useState, useMemo, useCallback } from 'react';

import { getIndisSearchRefundData } from '@/components/tax-refund/common/data/indis-search-refund-data';
import { getIndividualRefundData } from '@/components/tax-refund/common/data/individual-refund-data';
import { getIndisJointCertificateErrorContent } from '@/components/tax-refund/common/utils/getJointCertificateErrorContent';
import JointCertificateSelectView from '@/components/tax-refund/common/view/JointCertificateSelectView';
import { ErrorDialogContent } from '@/components/tax-refund/individual/JointCertificate/select/IndisSelectJoinCertificateErrorDialog';
import { 홈택스공동인증서등록가이드주소, 홈택스아이디생성주소 } from '@/lib/constants/link';
import { COMMON_PATHS, INDIVIDUAL_PATHS } from '@/lib/constants/paths';
import { useCheckHometaxAccount } from '@/lib/hooks/api/mutation/use-check-hometax-account';

const IndisSelectJoinCertificateContent = () => {
  const router = useRouter();
  const { openWindow } = useWindowOpen();
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false);
  const [errorIsOpenSelectAfterHandlePrimaryButton, setErrorIsOpenSelectAfterHandlePrimaryButton] = useState(false);
  const [errorDialogContent, setErrorDialogContent] = useState<ErrorDialogContent | null>(null);
  const { handleCheckHometaxAccount, isInFlight: isCheckJoinCertiLoading } = useCheckHometaxAccount();
  const indisRefundData = getIndividualRefundData();
  const indisSearchRefundData = getIndisSearchRefundData();

  const loginHometax = useCallback(
    (data: { signCert: string; signPri: string; signPw: string }) => {
      if (!indisRefundData) return;

      handleCheckHometaxAccount({
        variables: {
          corpType: 'INDIS',
          loginType: 'CommonCert',
          certificate: {
            signCert: data.signCert,
            signPri: data.signPri,
            signPw: data.signPw
          },
          tin: indisSearchRefundData?.tin
        },
        errorAction: (errorType) => {
          setErrorDialogContent(getIndisJointCertificateErrorContent(errorType));
          setIsOpenErrorDialog(true);
        }
      });
    },
    [handleCheckHometaxAccount, indisRefundData, indisSearchRefundData]
  );

  const moveJointCertificateInstallPage = useCallback(() => {
    router.push({
      pathname: INDIVIDUAL_PATHS.개인공동인증서프로그램설치판단,
      query: router.query
    });
  }, [router]);

  const errorHandleClickPrimaryButton = useCallback(() => {
    switch (errorDialogContent?.errorCode) {
      case 'ERR_HOMETAX_NO_JUMIN_ID':
      case 'ERR_HOMETAX_EXPIRE_CERT':
        setIsOpenErrorDialog(false);
        setErrorIsOpenSelectAfterHandlePrimaryButton(true);
        break;
      case 'ERR_HOMETAX_NO_REGIST_CERT':
        openWindow(홈택스공동인증서등록가이드주소);
        break;
      case 'ERR_HOMETAX_NO_USER':
        openWindow(홈택스아이디생성주소);
        break;
      default:
        router.push({
          pathname: COMMON_PATHS.환급조회목록,
          query: router.query
        });
        break;
    }
  }, [errorDialogContent?.errorCode, openWindow, router]);

  const errorHandleClickSecondaryButton = useCallback(() => {
    setIsOpenErrorDialog(false);
  }, []);

  const viewController = useMemo(
    () => ({
      isCheckJoinCertiLoading: isCheckJoinCertiLoading,
      loginHometax,
      isOpenErrorDialog,
      moveJointCertificateInstallPage,
      errorTitle: errorDialogContent?.title ?? '',
      errorMessage: errorDialogContent?.message ?? '',
      errorPrimaryButton: errorDialogContent?.primaryButtonText ?? '',
      errorSecondaryButton: errorDialogContent?.secondaryButtonText,
      errorIsOpenSelectAfterHandlePrimaryButton,
      errorHandleClickPrimaryButton,
      errorHandleClickSecondaryButton,
      shouldValidateRefundData: true
    }),
    [
      isCheckJoinCertiLoading,
      loginHometax,
      isOpenErrorDialog,
      moveJointCertificateInstallPage,
      errorDialogContent,
      errorIsOpenSelectAfterHandlePrimaryButton,
      errorHandleClickPrimaryButton,
      errorHandleClickSecondaryButton
    ]
  );

  return <JointCertificateSelectView viewController={viewController} />;
};

export default IndisSelectJoinCertificateContent;
